import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'warning' | 'danger';

export interface ToastPayload {
  title: string;
  message: string;
  type?: ToastType;
  durationMs?: number;
}

export interface ToastItem {
  id: number;
  title: string;
  message: string;
  type: ToastType;
  durationMs: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly toasts = signal<ToastItem[]>([]);

  private nextId = 1;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  show(payload: ToastPayload): number {
    const id = this.nextId;
    this.nextId += 1;

    const toast: ToastItem = {
      id,
      title: payload.title,
      message: payload.message,
      type: payload.type ?? 'success',
      durationMs: payload.durationMs ?? 5000,
    };

    this.toasts.update((current) => [...current, toast]);

    if (toast.durationMs > 0) {
      const timerId = setTimeout(() => {
        this.remove(id);
      }, toast.durationMs);
      this.timers.set(id, timerId);
    }

    return id;
  }

  success(title: string, message: string, durationMs = 5000): number {
    return this.show({ title, message, type: 'success', durationMs });
  }

  warning(title: string, message: string, durationMs = 5000): number {
    return this.show({ title, message, type: 'warning', durationMs });
  }

  danger(title: string, message: string, durationMs = 7000): number {
    return this.show({ title, message, type: 'danger', durationMs });
  }

  remove(id: number): void {
    const timerId = this.timers.get(id);
    if (timerId) {
      clearTimeout(timerId);
      this.timers.delete(id);
    }

    this.toasts.update((current) => current.filter((toast) => toast.id !== id));
  }

  clear(): void {
    for (const timerId of this.timers.values()) {
      clearTimeout(timerId);
    }
    this.timers.clear();
    this.toasts.set([]);
  }
}
